// The state behind every create/edit/delete modal in the app.
//
// Nine modals each hand-wrote the same five things: a form-state object, an
// `initial(editing)` function, a `useEffect` that resets on open, a `patch()`
// merger, and a `submit()` that branched on create-vs-update. They also each
// decided independently whether to warn before discarding unsaved edits — and
// seven of them decided not to, so a stray backdrop click threw the edit away.
//
// This hook owns that state. `EntityFormModal` owns the chrome around it. They
// are separate so a modal with an unusual body can take the hook alone.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { describeApiError } from "@/platform/client/api.js";

interface EntityFormOptions<T, F> {
  /** Whether the modal is open — the form resets on each opening. */
  open: boolean;
  /** The row being edited, or null to create a new one. */
  editing: T | null;
  /** Build the form state. Called on every open, so it must be pure. */
  initial: (editing: T | null) => F;
  /** Persist. Receives the form state; throwing surfaces the error in the modal. */
  save: (form: F, editing: T | null) => Promise<unknown>;
  /** Remove the row being edited. Absent means the modal has no delete action. */
  remove?: (editing: T) => Promise<unknown>;
  /** Message when `save` throws something that isn't an ApiError. */
  saveErrorMessage?: string;
  /** Called after a successful save or delete. */
  onClose: () => void;
}

export interface EntityForm<T, F> {
  form: F;
  /** Merge a partial update into the form state. */
  patch: (p: Partial<F>) => void;
  /** True once the form differs from what it opened with. */
  dirty: boolean;
  editing: T | null;
  saving: boolean;
  deleting: boolean;
  error: string | null;
  submit: () => Promise<void>;
  remove: (() => Promise<void>) | undefined;
  /**
   * The close path for the backdrop, Escape and Cancel. Returns false when the
   * form is dirty, meaning the caller must confirm before discarding.
   */
  requestClose: () => boolean;
  /** Discard unconditionally, after the caller has confirmed. */
  discard: () => void;
}

export function useEntityForm<T, F extends object>({
  open,
  editing,
  initial,
  save,
  remove,
  saveErrorMessage = "Could not save",
  onClose,
}: EntityFormOptions<T, F>): EntityForm<T, F> {
  const [form, setForm] = useState<F>(() => initial(editing));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const openedWith = useRef<F>(form);

  // Deliberately depends on `open` and `editing` only. `initial` is typically an
  // inline closure with a new identity every render, so including it would reset
  // the form on every keystroke; the form is meant to reset when the modal opens
  // or the row being edited changes, and at no other time.
  const initialRef = useRef(initial);
  initialRef.current = initial;
  useEffect(() => {
    if (!open) return;
    const next = initialRef.current(editing);
    setForm(next);
    openedWith.current = next;
    setError(null);
    setSaving(false);
    setDeleting(false);
  }, [open, editing]);

  const patch = useCallback((p: Partial<F>) => setForm((cur) => ({ ...cur, ...p })), []);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(openedWith.current), [form]);

  const submit = useCallback(async () => {
    setError(null);
    setSaving(true);
    try {
      await save(form, editing);
      onClose();
    } catch (err) {
      setError(describeApiError(err, saveErrorMessage));
    } finally {
      setSaving(false);
    }
  }, [form, editing, save, saveErrorMessage, onClose]);

  const removeFn = useMemo(() => {
    if (!remove || !editing) return undefined;
    return async () => {
      setError(null);
      setDeleting(true);
      try {
        await remove(editing);
        onClose();
      } catch (err) {
        setError(describeApiError(err, "Could not delete"));
        throw err; // keeps the confirmation dialog open
      } finally {
        setDeleting(false);
      }
    };
  }, [remove, editing, onClose]);

  const requestClose = useCallback(() => {
    if (dirty) return false;
    onClose();
    return true;
  }, [dirty, onClose]);

  const discard = useCallback(() => {
    setForm(openedWith.current);
    onClose();
  }, [onClose]);

  return {
    form,
    patch,
    dirty,
    editing,
    saving,
    deleting,
    error,
    submit,
    remove: removeFn,
    requestClose,
    discard,
  };
}
